/** @graphql agg **/
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';

/**
 * Single source of truth for the Teamwork Graph entity types the connections
 * byline treats as incoming or related links, and the field each one carries its URL in.
 *
 * These are members of the `GraphStoreCypherQueryV2AriNodeUnion` that
 * `useIncomingLinksQuery` and `useRelatedContentQuery` return. The union has no shared
 * interface, so {@link IncomingLinkNodeFragment} selects a `... on <Type>` fragment per
 * member and both queries spread it unmasked; this catalog is the authoritative list of
 * which members it selects and with which field. The `incomingLinkTypes-test`
 * drift-guard asserts the compiled fragment and this catalog stay in lockstep.
 *
 * Every selection is aliased `<Type>_<field>` (see {@link getIncomingLinkUrlAlias}).
 * That is a requirement, not a style choice: GraphQL's same-response-shape rule
 * rejects one response name `url` carrying `String` (Townsquare, all `External*`)
 * and `URL` (Mercury, Bitbucket, DevOps) across sibling fragments, and `links`
 * resolves to a different object type on every Confluence type.
 *
 * Scope is deliberately limited to content-like entities. People/org peers
 * (workers, teams, customers, organisations, positions) are intentionally omitted
 * so they don't surface as "links to this page", as are types the schema gives no
 * URL at all (sprints, statuses, issue types, worklogs, groups).
 */
export const INCOMING_LINK_URL_FIELDS = {
	// Confluence — `links.webUi` is site-relative and needs `links.base` prepended.
	ConfluenceBlogPost: 'links',
	ConfluenceDatabase: 'links',
	ConfluenceEmbed: 'links',
	ConfluenceFolder: 'links',
	ConfluenceFooterComment: 'links',
	ConfluenceInlineComment: 'links',
	ConfluencePage: 'links',
	ConfluenceSlide: 'links',
	ConfluenceSpace: 'links',
	ConfluenceWhiteboard: 'links',
	// Jira
	JiraIssue: 'webUrl',
	JiraPlatformComment: 'webUrl',
	JiraProject: 'webUrl',
	JiraServiceManagementComment: 'webUrl',
	// Atlas (townsquare)
	TownsquareComment: 'url',
	TownsquareGoal: 'url',
	TownsquareGoalUpdate: 'url',
	TownsquareProject: 'url',
	TownsquareProjectUpdate: 'url',
	// Loom
	LoomComment: 'url',
	LoomSpace: 'url',
	LoomVideo: 'url',
	// Focus (mercury)
	MercuryAsk: 'url',
	MercuryFocusArea: 'url',
	// Bitbucket and the DevOps graph
	BitbucketRepository: 'href',
	DeploymentSummary: 'url',
	DevOpsDesign: 'url',
	DevOpsDocument: 'url',
	DevOpsOperationsIncidentDetails: 'url',
	DevOpsOperationsPostIncidentReviewDetails: 'url',
	DevOpsProjectDetails: 'url',
	DevOpsPullRequestDetails: 'url',
	DevOpsRepository: 'url',
	DevOpsSecurityVulnerabilityDetails: 'url',
	// Compass
	CompassComponent: 'url',
	// Third-party ("External") peers
	ExternalBranch: 'url',
	ExternalBuildInfo: 'url',
	ExternalCalendarEvent: 'url',
	ExternalComment: 'url',
	ExternalCommit: 'url',
	ExternalConversation: 'url',
	ExternalDashboard: 'url',
	ExternalDataTable: 'url',
	ExternalDeal: 'url',
	ExternalDeployment: 'url',
	ExternalDesign: 'url',
	ExternalDocument: 'url',
	ExternalMessage: 'url',
	ExternalPullRequest: 'url',
	ExternalRemoteLink: 'url',
	ExternalRepository: 'url',
	ExternalSoftwareService: 'url',
	ExternalSpace: 'url',
	ExternalTest: 'url',
	ExternalVideo: 'url',
	ExternalVulnerability: 'url',
	ExternalWorkItem: 'url',
} as const;

/** `__typename` of a curated incoming link entity. */
export type IncomingLinkTypename = keyof typeof INCOMING_LINK_URL_FIELDS;

/** The curated `__typename`s the byline query selects a fragment for. */
export const INCOMING_LINK_TYPENAMES = Object.keys(
	INCOMING_LINK_URL_FIELDS,
) as IncomingLinkTypename[];

/** Narrows an arbitrary `__typename` to one of the curated incoming link types. */
export const isCuratedIncomingLinkTypename = (
	typename: string | null | undefined,
): typename is IncomingLinkTypename => typename != null && typename in INCOMING_LINK_URL_FIELDS;

/**
 * The response name the query selects a type's URL field under. Both the query
 * text and the reader derive from this, so a type can never be selected under one
 * alias and read under another.
 */
export const getIncomingLinkUrlAlias = (typename: IncomingLinkTypename): string =>
	`${typename}_${INCOMING_LINK_URL_FIELDS[typename]}`;

/**
 * The selection both graph queries spread unmasked on a node's `data`: each catalogued
 * type's URL field, under the alias {@link getIncomingLinkUrlAlias} builds for it.
 */
export const IncomingLinkNodeFragment: GraphQLTaggedNode = graphql`
	fragment incomingLinkTypes_node on GraphStoreCypherQueryV2AriNodeUnion {
		__typename
		# Confluence: webUi is site-relative, so base comes with it.
		... on ConfluenceBlogPost {
			ConfluenceBlogPost_links: links {
				base
				webUi
			}
		}
		... on ConfluenceDatabase {
			ConfluenceDatabase_links: links {
				base
				webUi
			}
		}
		... on ConfluenceEmbed {
			ConfluenceEmbed_links: links {
				base
				webUi
			}
		}
		... on ConfluenceFolder {
			ConfluenceFolder_links: links {
				base
				webUi
			}
		}
		... on ConfluenceFooterComment {
			ConfluenceFooterComment_links: links {
				base
				webUi
			}
		}
		... on ConfluenceInlineComment {
			ConfluenceInlineComment_links: links {
				base
				webUi
			}
		}
		... on ConfluencePage {
			ConfluencePage_links: links {
				base
				webUi
			}
		}
		... on ConfluenceSlide {
			ConfluenceSlide_links: links {
				base
				webUi
			}
		}
		... on ConfluenceSpace {
			ConfluenceSpace_links: links {
				base
				webUi
			}
		}
		... on ConfluenceWhiteboard {
			ConfluenceWhiteboard_links: links {
				base
				webUi
			}
		}
		# Jira
		... on JiraIssue {
			JiraIssue_webUrl: webUrl
		}
		... on JiraPlatformComment {
			JiraPlatformComment_webUrl: webUrl
		}
		... on JiraProject {
			JiraProject_webUrl: webUrl
		}
		... on JiraServiceManagementComment {
			JiraServiceManagementComment_webUrl: webUrl
		}
		# Atlas (townsquare)
		... on TownsquareComment {
			TownsquareComment_url: url
		}
		... on TownsquareGoal {
			TownsquareGoal_url: url
		}
		... on TownsquareGoalUpdate {
			TownsquareGoalUpdate_url: url
		}
		... on TownsquareProject {
			TownsquareProject_url: url
		}
		... on TownsquareProjectUpdate {
			TownsquareProjectUpdate_url: url
		}
		# Loom
		... on LoomComment {
			LoomComment_url: url
		}
		... on LoomSpace {
			LoomSpace_url: url
		}
		... on LoomVideo {
			LoomVideo_url: url
		}
		# Focus (mercury)
		... on MercuryAsk {
			MercuryAsk_url: url
		}
		... on MercuryFocusArea {
			MercuryFocusArea_url: url
		}
		# Bitbucket and the DevOps graph
		... on BitbucketRepository {
			BitbucketRepository_href: href
		}
		... on DeploymentSummary {
			DeploymentSummary_url: url
		}
		... on DevOpsDesign {
			DevOpsDesign_url: url
		}
		... on DevOpsDocument {
			DevOpsDocument_url: url
		}
		... on DevOpsOperationsIncidentDetails {
			DevOpsOperationsIncidentDetails_url: url
		}
		... on DevOpsOperationsPostIncidentReviewDetails {
			DevOpsOperationsPostIncidentReviewDetails_url: url
		}
		... on DevOpsProjectDetails {
			DevOpsProjectDetails_url: url
		}
		... on DevOpsPullRequestDetails {
			DevOpsPullRequestDetails_url: url
		}
		... on DevOpsRepository {
			DevOpsRepository_url: url
		}
		... on DevOpsSecurityVulnerabilityDetails {
			DevOpsSecurityVulnerabilityDetails_url: url
		}
		# Compass
		... on CompassComponent {
			CompassComponent_url: url
		}
		# Third-party ("External") peers
		... on ExternalBranch {
			ExternalBranch_url: url
		}
		... on ExternalBuildInfo {
			ExternalBuildInfo_url: url
		}
		... on ExternalCalendarEvent {
			ExternalCalendarEvent_url: url
		}
		... on ExternalComment {
			ExternalComment_url: url
		}
		... on ExternalCommit {
			ExternalCommit_url: url
		}
		... on ExternalConversation {
			ExternalConversation_url: url
		}
		... on ExternalDashboard {
			ExternalDashboard_url: url
		}
		... on ExternalDataTable {
			ExternalDataTable_url: url
		}
		... on ExternalDeal {
			ExternalDeal_url: url
		}
		... on ExternalDeployment {
			ExternalDeployment_url: url
		}
		... on ExternalDesign {
			ExternalDesign_url: url
		}
		... on ExternalDocument {
			ExternalDocument_url: url
		}
		... on ExternalMessage {
			ExternalMessage_url: url
		}
		... on ExternalPullRequest {
			ExternalPullRequest_url: url
		}
		... on ExternalRemoteLink {
			ExternalRemoteLink_url: url
		}
		... on ExternalRepository {
			ExternalRepository_url: url
		}
		... on ExternalSoftwareService {
			ExternalSoftwareService_url: url
		}
		... on ExternalSpace {
			ExternalSpace_url: url
		}
		... on ExternalTest {
			ExternalTest_url: url
		}
		... on ExternalVideo {
			ExternalVideo_url: url
		}
		... on ExternalVulnerability {
			ExternalVulnerability_url: url
		}
		... on ExternalWorkItem {
			ExternalWorkItem_url: url
		}
	}
`;
